import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { Paperclip, Send, Code, X, FileText, Image, Video, File, StickyNote, AtSign } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessageComposer = ({ channelId, parentId = null, onSent }) => {
  const { user } = useAuth();
  const { sendMessage, uploadFile, sendTyping } = useChat();
  const [content, setContent] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [codeLanguage, setCodeLanguage] = useState('javascript');
  const [codeContent, setCodeContent] = useState('');
  // @mentions state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);

  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const docInputRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const mentionBoxRef = useRef(null);

  // Fetch users for mentions
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API}/users`);
        setMentionUsers(res.data.users || []);
      } catch (e) {
        console.error('Fetch users for mentions:', e);
      }
    };
    fetchUsers();
  }, []);

  const handleTyping = useCallback(() => {
    if (!channelId) return;
    sendTyping(channelId, true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(channelId, false);
    }, 2000);
  }, [channelId, sendTyping]);

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of selectedFiles) {
        const result = await uploadFile(file);
        uploaded.push(result);
      }
      setFiles(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) attached`);
    } catch (e) {
      toast.error('File upload failed');
      console.error('Upload error:', e);
    }
    setUploading(false);
    // Reset all file inputs
    [fileInputRef, imageInputRef, videoInputRef, docInputRef].forEach(ref => {
      if (ref.current) ref.current.value = '';
    });
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // --- @Mention logic ---
  const getFilteredMentionUsers = () => {
    if (!mentionQuery) return mentionUsers.filter(u => u.user_id !== user?.user_id).slice(0, 8);
    const q = mentionQuery.toLowerCase();
    return mentionUsers
      .filter(u => u.user_id !== user?.user_id)
      .filter(u =>
        (u.username || '').toLowerCase().includes(q) ||
        (u.display_name || '').toLowerCase().includes(q)
      )
      .slice(0, 8);
  };

  const insertMention = (mentionUser) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Find the @ position
    const beforeCursor = content.substring(0, cursorPosition);
    const atIdx = beforeCursor.lastIndexOf('@');
    if (atIdx === -1) return;

    const beforeAt = content.substring(0, atIdx);
    const afterCursor = content.substring(cursorPosition);
    const mentionText = `@${mentionUser.username} `;
    const newContent = beforeAt + mentionText + afterCursor;

    setContent(newContent);
    setShowMentions(false);
    setMentionQuery('');
    setMentionIndex(0);

    // Focus and set cursor after mention
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const newPos = beforeAt.length + mentionText.length;
        textarea.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleContentChange = (e) => {
    const value = e.target.value;
    const pos = e.target.selectionStart;
    setContent(value);
    setCursorPosition(pos);
    handleTyping();

    // Check for @ mention trigger
    const beforeCursor = value.substring(0, pos);
    const atIdx = beforeCursor.lastIndexOf('@');

    if (atIdx !== -1) {
      const charBeforeAt = atIdx > 0 ? beforeCursor[atIdx - 1] : ' ';
      // Only trigger if @ is at start of message or preceded by whitespace
      if (atIdx === 0 || /\s/.test(charBeforeAt)) {
        const query = beforeCursor.substring(atIdx + 1);
        // Check no space in query (after @)
        if (!query.includes(' ') && query.length < 20) {
          setMentionQuery(query);
          setShowMentions(true);
          setMentionIndex(0);
          return;
        }
      }
    }
    setShowMentions(false);
  };

  const handleSend = async () => {
    const hasNote = showNoteEditor && noteTitle.trim();
    if (!content.trim() && files.length === 0 && !codeContent.trim() && !hasNote) return;
    if (!channelId) return;

    try {
      const attachments = files.map(f => ({
        file_id: f.file_id,
        original_name: f.original_name,
        file_type: f.file_type,
        file_size: f.file_size,
        url: f.url
      }));

      const codeSnippet = codeContent.trim() ? { language: codeLanguage, code: codeContent } : null;

      // Extract mentions from content
      const mentionRegex = /@(\w+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(content)) !== null) {
        const mentionedUser = mentionUsers.find(
          u => u.username === match[1]
        );
        if (mentionedUser) {
          mentions.push({
            user_id: mentionedUser.user_id,
            username: mentionedUser.username,
            display_name: mentionedUser.display_name
          });
        }
      }

      let messageType = 'text';
      let noteData = null;
      if (hasNote) {
        messageType = 'note';
        noteData = { title: noteTitle, body: noteBody };
      } else if (codeSnippet) {
        messageType = 'code';
      } else if (files.length > 0) {
        messageType = 'file';
      }

      await sendMessage(channelId, content, parentId, messageType, attachments, codeSnippet, mentions, noteData);

      setContent('');
      setFiles([]);
      setCodeContent('');
      setShowCodeEditor(false);
      setShowNoteEditor(false);
      setNoteTitle('');
      setNoteBody('');
      sendTyping(channelId, false);
      if (onSent) onSent();
    } catch (e) {
      toast.error('Failed to send message');
    }
  };

  const handleKeyDown = (e) => {
    // Handle mention navigation
    if (showMentions) {
      const filtered = getFilteredMentionUsers();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(prev => (prev + 1) % filtered.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(prev => (prev - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (filtered.length > 0) {
          e.preventDefault();
          insertMention(filtered[mentionIndex]);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }

    // Normal Enter to send
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const languages = ['javascript', 'python', 'java', 'cpp', 'html', 'css', 'sql', 'bash', 'json', 'typescript', 'go', 'rust'];

  const filteredMentionUsers = getFilteredMentionUsers();

  return (
    <div className="sticky bottom-0 px-3 sm:px-5 pb-3 pt-1 bg-background" data-testid="message-composer">
     <div className="bg-card border border-border rounded-xl shadow-[var(--shadow-sm)]">
      {/* File attachments preview */}
      {files.length > 0 && (
        <div className="px-3 pt-2 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1.5 px-2 py-1 bg-muted rounded-md text-xs">
              {f.file_type?.startsWith('image/') ? <Image className="w-3.5 h-3.5 text-muted-foreground" /> :
               f.file_type?.startsWith('video/') ? <Video className="w-3.5 h-3.5 text-muted-foreground" /> :
               <FileText className="w-3.5 h-3.5 text-muted-foreground" />}
              <span className="truncate max-w-[120px]">{f.original_name}</span>
              <button onClick={() => removeFile(i)} className="hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Code snippet editor */}
      {showCodeEditor && (
        <div className="px-3 pt-2">
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 border-b border-border">
              <Code className="w-3.5 h-3.5 text-muted-foreground" />
              <select
                value={codeLanguage}
                onChange={(e) => setCodeLanguage(e.target.value)}
                className="text-xs bg-transparent border-none focus:outline-none text-muted-foreground"
                data-testid="code-language-select"
              >
                {languages.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <div className="flex-1" />
              <button onClick={() => { setShowCodeEditor(false); setCodeContent(''); }} className="text-xs text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <textarea
              value={codeContent}
              onChange={(e) => setCodeContent(e.target.value)}
              placeholder="Paste your code here..."
              className="w-full min-h-[100px] p-3 bg-card text-sm font-mono resize-none focus:outline-none"
              data-testid="code-snippet-input"
            />
          </div>
        </div>
      )}

      {/* Note editor */}
      {showNoteEditor && (
        <div className="px-3 pt-2">
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[hsl(var(--accent))]/30 border-b border-border">
              <StickyNote className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Note</span>
              <div className="flex-1" />
              <button onClick={() => { setShowNoteEditor(false); setNoteTitle(''); setNoteBody(''); }} className="text-xs text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="p-3 space-y-2 bg-card">
              <input
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                placeholder="Note title..."
                className="w-full bg-transparent text-sm font-semibold focus:outline-none border-b border-border pb-2"
                data-testid="note-title-input"
              />
              <textarea
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                placeholder="Write your note content..."
                className="w-full min-h-[80px] bg-transparent text-sm resize-none focus:outline-none"
                data-testid="note-body-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-2 relative z-10">
        {/* Hidden file inputs for different types */}
        <input type="file" ref={imageInputRef} onChange={handleFileSelect} className="hidden" accept="image/*" multiple data-testid="image-input" />
        <input type="file" ref={videoInputRef} onChange={handleFileSelect} className="hidden" accept="video/*" multiple data-testid="video-input" />
        <input type="file" ref={docInputRef} onChange={handleFileSelect} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar" multiple data-testid="doc-input" />
        <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" multiple data-testid="file-input" />

        {/* Attachment dropdown menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={uploading}
              data-testid="composer-attach-button"
            >
              <Paperclip className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48" data-testid="attach-menu">
            <DropdownMenuItem onClick={() => imageInputRef.current?.click()} data-testid="attach-image">
              <Image className="w-4 h-4 mr-2 text-[hsl(var(--presence-online))]" />
              <span>Image</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => videoInputRef.current?.click()} data-testid="attach-video">
              <Video className="w-4 h-4 mr-2 text-primary" />
              <span>Video</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => docInputRef.current?.click()} data-testid="attach-document">
              <FileText className="w-4 h-4 mr-2 text-[hsl(var(--presence-away))]" />
              <span>Document</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => fileInputRef.current?.click()} data-testid="attach-any-file">
              <File className="w-4 h-4 mr-2 text-muted-foreground" />
              <span>Any File</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => { setShowCodeEditor(!showCodeEditor); setShowNoteEditor(false); }}
              data-testid="composer-code-button"
            >
              <Code className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Code snippet</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => { setShowNoteEditor(!showNoteEditor); setShowCodeEditor(false); }}
              data-testid="composer-note-button"
            >
              <StickyNote className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Create note</TooltipContent>
        </Tooltip>

        <div className="flex-1 mx-2 relative">
          {/* @Mention autocomplete dropdown */}
          {showMentions && filteredMentionUsers.length > 0 && (
            <div
              ref={mentionBoxRef}
              className="absolute bottom-full left-0 mb-1 w-64 max-h-52 overflow-y-auto bg-popover border border-border rounded-lg shadow-lg z-50"
              data-testid="mention-dropdown"
            >
              <div className="p-1">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  Members
                </div>
                {filteredMentionUsers.map((u, idx) => (
                  <button
                    key={u.user_id}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                      idx === mentionIndex
                        ? 'bg-[hsl(var(--sidebar-active))] text-[hsl(var(--sidebar-active-foreground))]'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => insertMention(u)}
                    onMouseEnter={() => setMentionIndex(idx)}
                    data-testid={`mention-user-${u.username}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {(u.display_name || u.username || '?')[0]}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <span className="font-medium truncate block">{u.display_name || u.username}</span>
                      <span className="text-xs text-muted-foreground">@{u.username}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={content}
            onChange={handleContentChange}
            onKeyDown={handleKeyDown}
            onClick={(e) => setCursorPosition(e.target.selectionStart)}
            placeholder={parentId ? 'Reply... (use @ to mention)' : 'Type a message... (use @ to mention)'}
            className="w-full min-h-[36px] max-h-[120px] resize-none border-0 bg-transparent focus:outline-none focus:ring-0 text-sm leading-5 py-2"
            rows={1}
            aria-label="Message"
            data-testid="composer-input"
          />
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              className="h-8 w-8"
              onClick={handleSend}
              disabled={(!content.trim() && files.length === 0 && !codeContent.trim() && !(showNoteEditor && noteTitle.trim())) || uploading}
              data-testid="composer-send-button"
            >
              <Send className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Send (Enter)</TooltipContent>
        </Tooltip>
      </div>
     </div>
    </div>
  );
};

export default MessageComposer;
